import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import { REELS_PLAN_MAX_DAYS, REELS_PLAN_MIN_DAYS } from 'fym-shared/src/music24/reelsPlan';
import { TypedMemo } from '../../../../../common/types';
import type {
  ReelsPlanCadence,
  ReelsPlanPostsPerDay,
  ReelsPlanPreview,
  ReelsPlanSettings,
} from '../../../../../../redux/api/fymAPI.types';
import { useMusic24Capabilities } from '../../../../hooks/useMusic24Capabilities';
import { Panel, PanelHead } from '../../../../components/primitives/Panel';
import type { SerializedSocialChannel, SocialPlatform } from '../../../types';
import { usePlanChannelPick } from '../usePlanChannelPick';
import { ReelsPlanCadencePicker } from './ReelsPlanCadencePicker';
import { ReelsPlanChannelsRow } from './ReelsPlanChannelsRow';
import { ReelsPlanDayStrip } from './ReelsPlanDayStrip';
import { ReelsPlanFoot } from './ReelsPlanFoot';
import { ReelsPlanLengthPicker } from './ReelsPlanLengthPicker';
import { ReelsPlanPostsPicker } from './ReelsPlanPostsPicker';
import { ReelsPlanPostingModes } from './ReelsPlanPostingModes';
import { ReelsPlanUpsell } from './ReelsPlanUpsell';
import { reelsPlanMessages } from './reelsPlanMessages';
import {
  defaultReelsPlanSettings,
  isReelsPlanLength,
  isReelsPlanStart,
  reelsPlanMaxPostsPerDay,
  reelsPlanMissingCredits,
  reelsPlanPlatforms,
  reelsPlanShortfalls,
  reelsPlanSummary,
} from './reelsPlanModel';
import { useReelsPlan } from './useReelsPlan';
import { useReelsPlanPreview } from './useReelsPlanPreview';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the AI reels plan; each part uses its own slice of it */
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanSetupProps {
  accountId: number;
  catalogItemUuid: string;
  songTitle: string;
  /** The usable channels of the artist (connected and manual). */
  platforms: SocialPlatform[];
  /** The rows of those channels: the setup says which post by themselves (QA finding B14). */
  channels?: SerializedSocialChannel[];
  /** `YYYY-MM-DD` of today in the time zone of the artist. */
  today: string;
  /** False for the demo artist and a read-only artist: every control is off. */
  canAct: boolean;
  /** The setup under the closed line of an older plan: "Start the next campaign". */
  isNext: boolean;
}

/**
 * The setup of an AI reels plan (design rollout-plan f1, f2, f5): the length, how often, the posts
 * a day, the start day and the channels; the day strip; and the foot with the numbers of the dry
 * run. "Make the plan" checks the balance first, then makes every reel with one charge. The posts
 * a day above the plan (Free and the trial: 1) show locked with the plan offer.
 */
const ReelsPlanSetupComponent: React.FC<ReelsPlanSetupProps> = ({
  accountId,
  catalogItemUuid,
  songTitle,
  platforms,
  channels,
  today,
  canAct,
  isNext,
}) => {
  const intl = useIntl();
  const [settings, setSettings] = useState<ReelsPlanSettings>(() => defaultReelsPlanSettings(today));
  const reelPlatforms = useMemo(() => reelsPlanPlatforms(platforms), [platforms]);
  const { selected, toggle } = usePlanChannelPick(reelPlatforms);
  const isLengthValid = isReelsPlanLength(settings.days);
  const isValid = isLengthValid && isReelsPlanStart(settings.startDay, today);
  // The plan was made: the numbers of the click stay until the run sheet takes the place of the
  // setup, and no dry run asks again (it would answer "the song has a plan already").
  const [made, setMade] = useState<ReelsPlanPreview | null>(null);
  const live = useReelsPlanPreview({
    accountId,
    catalogItemUuid,
    settings,
    platforms: selected,
    isValid,
    skip: !canAct || made !== null,
  });
  const preview = made ?? live.preview;
  const { isChecking, hasFailed } = live;
  const capabilities = useMusic24Capabilities();
  const maxPostsPerDay = reelsPlanMaxPostsPerDay(preview, capabilities.reelsPlanMaxPostsPerDay);
  const { gate, make, isMaking, makeFeedback, clearFeedback } = useReelsPlan({ accountId, catalogItemUuid });

  const change = useCallback(
    (part: Partial<ReelsPlanSettings>) => {
      clearFeedback();
      setSettings((current) => ({ ...current, ...part }));
    },
    [clearFeedback]
  );
  const setDays = useCallback((days: number) => change({ days }), [change]);
  const setCadence = useCallback((cadence: ReelsPlanCadence) => change({ cadence }), [change]);
  const setPostsPerDay = useCallback((postsPerDay: ReelsPlanPostsPerDay) => change({ postsPerDay }), [change]);
  const setStart = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => change({ startDay: event.target.value }),
    [change]
  );
  const onMake = useCallback(() => {
    const credits = reelsPlanSummary(preview)?.credits;
    if (credits === undefined || preview === undefined) {
      return;
    }
    make(settings, selected, credits, reelsPlanMissingCredits(preview))
      .then((ok) => (ok ? setMade(preview) : undefined))
      .catch(console.error);
  }, [make, preview, selected, settings]);

  const shortfalls = useMemo(() => reelsPlanShortfalls(preview), [preview]);
  const hasShortfall = shortfalls.credits !== null || shortfalls.posts !== null;

  const title = intl.formatMessage(reelsPlanMessages.title);
  return (
    <Panel as="section" aria-label={title} data-testid="reels-plan-setup">
      <PanelHead
        title={title}
        subtitle={intl.formatMessage(isNext ? reelsPlanMessages.subtitleNext : reelsPlanMessages.subtitle)}
      />
      <div className={styles.set}>
        <span className={styles.lab}>{intl.formatMessage(reelsPlanMessages.length)}</span>
        <ReelsPlanLengthPicker days={settings.days} disabled={!canAct} onChange={setDays} />
        <span className={styles.lab}>{intl.formatMessage(reelsPlanMessages.cadence)}</span>
        <ReelsPlanCadencePicker cadence={settings.cadence} disabled={!canAct} onChange={setCadence} />
        <span className={styles.lab}>{intl.formatMessage(reelsPlanMessages.postsPerDay)}</span>
        <ReelsPlanPostsPicker
          postsPerDay={settings.postsPerDay}
          maxPostsPerDay={maxPostsPerDay}
          disabled={!canAct}
          onChange={setPostsPerDay}
        />
        {(settings.postsPerDay > 1 || maxPostsPerDay < 2) && (
          <p className={styles.setHint} data-testid="reels-plan-posts-hint">
            {intl.formatMessage(
              maxPostsPerDay < 2 ? reelsPlanMessages.postsPerDayLocked : reelsPlanMessages.postsPerDayHint
            )}
          </p>
        )}
        <span className={styles.lab}>{intl.formatMessage(reelsPlanMessages.starts)}</span>
        <div className={styles.seg}>
          <Input
            type="date"
            bsSize="sm"
            className={styles.field}
            min={today}
            value={settings.startDay}
            disabled={!canAct}
            aria-label={intl.formatMessage(reelsPlanMessages.starts)}
            onChange={setStart}
          />
        </div>
        <span className={styles.lab}>{intl.formatMessage(reelsPlanMessages.channels)}</span>
        <ReelsPlanChannelsRow platforms={reelPlatforms} selected={selected} disabled={!canAct} onToggle={toggle} />
        {channels !== undefined && <ReelsPlanPostingModes channels={channels} selected={selected} />}
      </div>
      <ReelsPlanDayStrip settings={settings} />
      <ReelsPlanFoot
        catalogItemUuid={catalogItemUuid}
        songTitle={songTitle}
        preview={preview}
        isChecking={isChecking}
        hasFailed={hasFailed}
        invalidNote={
          isLengthValid
            ? null
            : intl.formatMessage(reelsPlanMessages.lengthRange, { min: REELS_PLAN_MIN_DAYS, max: REELS_PLAN_MAX_DAYS })
        }
        canAct={canAct && isValid}
        gate={gate}
        isMaking={isMaking || made !== null}
        feedback={makeFeedback}
        onMake={onMake}
        upsell={
          canAct && made === null && hasShortfall ? (
            <ReelsPlanUpsell
              preview={preview}
              credits={shortfalls.credits}
              posts={shortfalls.posts}
              settings={settings}
              onDays={setDays}
              onBuyCredits={gate.offerCredits}
            />
          ) : undefined
        }
      />
    </Panel>
  );
};

export const ReelsPlanSetup = TypedMemo(ReelsPlanSetupComponent);
